/**
 * Email module navigation config consumed by `@quotech/ui`'s
 * `DashboardLayout`. Mirrors the shape every other standalone hub at
 * *.quotech.ai uses (qa, qdrive, crm, cfo, billing).
 *
 * Why most of this is intentionally empty:
 *   The email module is rendered by the standalone SAM-style
 *   `EmailLayout` from `@quotech/email-hub`. That layout owns its own
 *   inner sidebar (Compose, Inbox/Starred/Sent/Archived/Trash, Sync &
 *   Status, Email Settings, Tags & Rules, mailbox switcher, Outlook
 *   footer, Recent issues). Putting those same items in the OUTER
 *   DashboardLayout rail would duplicate them, which is exactly the
 *   regression user reported on 2026-05-13: "you instead put inbox etc
 *   in the left nav that is not where SAM's is..."
 *
 *   So the OUTER rail only carries cross-module navigation:
 *     - HUB_LINKS       → other hubs at *.quotech.ai (HUBS section)
 *     - MAIN_NAV_ITEMS  → Emails + Kanban (peer entries, mirrors
 *                         SAM's shell where Email Hub and Kanban sit
 *                         side-by-side). Emails anchors at /inbox;
 *                         Kanban points at the placeholder page
 *                         (`KanbanComingSoonPage`) until the real
 *                         kanban port lands as a separate MR.
 *   The INNER sidebar (owned by EmailLayout) carries email-only nav.
 *
 * Net layout:
 *   ┌──────────────┬──────────────────┬──────────────────────────┐
 *   │ DashboardLayout                  │ EmailLayout (standalone) │
 *   │ outer rail   │ inner sidebar    │ content area             │
 *   ├──────────────┤ ───────────────  ├ ─────────────────────────┤
 *   │ NAVIGATION   │ Compose CTA      │ Outlook/Gmail banner     │
 *   │  Emails      │ Inbox            │ <Outlet /> (Inbox / etc) │
 *   │  Kanban      │ Starred          │ Recent issues panel      │
 *   │              │ Sent             │                          │
 *   │ HUBS         │ Archived         │                          │
 *   │  Jobs Hub    │ Trash            │                          │
 *   │  CRM Hub     │ Sync & Status    │                          │
 *   │  Quote ...   │ Email Settings   │                          │
 *   │  ...         │ Tags & Rules     │                          │
 *   │              │ Outlook footer   │                          │
 *   └──────────────┴──────────────────┴──────────────────────────┘
 */

import { KanbanSquare, Mail, PencilRuler, UserCircle } from 'lucide-react'
import type { NavItem, ModuleConfig, HubId } from '@quotech/ui'
import { getExternalLinks } from '@quotech/ui'

// Module identity. Drives DashboardLayout's brand strip, HubSwitcher
// highlight, header pill, and accent classes throughout the outer
// chrome. Renamed from "Email Hub" to "Emails" per user feedback
// 2026-05-13 ("remove the email hub thing just have it as emails")
// and locked plural per the screenshot 2026-05-13 ("Rename email hub
// as just emails"). Matches HUB_REGISTRY.email.name in
// packages/ui/src/lib/hub-registry/definitions-core.ts so the HUBS
// section, HubSwitcher dropdown, and breadcrumbs all read the same.
export const MODULE_CONFIG: ModuleConfig = {
  id: 'email' as HubId,
  name: 'Emails',
  icon: Mail,
  accentColor: 'rose',
  accentGradient: 'from-rose-500 to-pink-600',
  accentBgClass: 'bg-rose-600',
  accentHoverClass: 'hover:bg-rose-700',
  accentTextClass: 'text-rose-600',
  accentBadgeBgClass: 'bg-rose-100',
  accentBadgeTextClass: 'text-rose-700',
  baseUrl: 'https://email.quotech.ai',
}

// Outer-rail MAIN section. TWO peer entries — Emails and Kanban — to
// match the user's screenshot 2026-05-13 of the Bravada Jobs Hub
// ("NAVIGATION → Dashboard / Active Jobs / Reports") and the SAM
// shell convention (apps/sam/src/shell/navigation.ts: Email Hub +
// Kanban as peer entries). The Emails entry anchors at /inbox (the
// email-hub default landing); the inner SAM-style EmailLayout owns
// folder navigation (Inbox/Starred/Sent/Archived/Trash) so the outer
// "Emails" item is purely a "go to email home" CTA, not a per-folder
// switcher.
//
// Kanban currently lands on `KanbanComingSoonPage` (stub linking to
// sam.quotech.ai/kanban). Porting SAM's full kanban
// (`apps/sam/src/features/kanban/KanbanPage.tsx`, ~450 LOC of
// SAM-specific tenant adapters) is a separate extraction MR — same
// shape as the @quotech/email-hub extraction, lifting the kanban
// tenant hooks, email adapter, and members adapter out of apps/sam
// into a `@quotech/kanban-hub` package.
export const MAIN_NAV_ITEMS: NavItem[] = [
  { id: 'emails', label: 'Emails', icon: Mail, href: '/inbox' },
  // Cross-org "Personal" workspace (FEAT-2026-06-22-CROSS-ORG-PERSONAL):
  // one structured inbox spanning every org the operator belongs to +
  // a board mirror their private cross-tag pins overlay onto. Peer entry
  // (renders in the DashboardLayout content area, not EmailLayout's inner
  // sidebar) since it deliberately spans orgs rather than one mailbox.
  { id: 'personal', label: 'Personal', icon: UserCircle, href: '/personal' },
  { id: 'kanban', label: 'Kanban', icon: KanbanSquare, href: 'https://newmark.quotech.ai/kanban' },
  // Drafting Studio — IR-driven drawing canvas (P1: manual draw + markup
  // + export). Mounts as a peer outer-rail entry like Kanban; renders in
  // the DashboardLayout content area (not inside EmailLayout's inner
  // sidebar). AI generation + Django persistence land in P2/P3. See
  // docs/scopes/DRAFTING_STUDIO_SCOPE.md.
  { id: 'drafting', label: 'Drafting', icon: PencilRuler, href: '/drafting' },
]

// Outer-rail TOOLS section. Empty for the email module — Sync &
// Status, Email Settings, Tags & Rules all live inside EmailLayout's
// own inner sidebar, where SAM exposes them. Pre-2026-05-13 these
// sat in DashboardLayout's secondary section, which created a
// duplicate "tools" surface user reported as confusing.
export const SECONDARY_NAV_ITEMS: NavItem[] = []

// Section header for the outer-rail MAIN list. "Navigation" matches
// the Bravada master dashboard convention seen on jobs.quotech.ai
// ("NAVIGATION → Dashboard / Active Jobs / Reports"), keeping the
// shell consistent across hubs instead of inventing a hub-specific
// "Modules" label.
export const SIDEBAR_SECTION_LABELS = {
  main: 'Navigation',
  secondary: 'Tools',
}

export const PAGE_TITLES: Record<string, string> = {
  '/inbox': 'Inbox',
  '/starred': 'Starred',
  '/sent': 'Sent',
  '/archived': 'Archived',
  '/trash': 'Trash',
  '/compose': 'Compose',
  '/settings': 'Email Settings',
  '/kanban': 'Kanban',
  '/drafting': 'Drafting',
}

// Cross-hub deep links — built from the canonical HUB_REGISTRY so the
// "switch to..." menu and HUBS section always reflect the real hub
// list without us maintaining a parallel array. `getExternalLinks`
// filters out the current hub automatically.
export const HUB_LINKS = getExternalLinks('email')
